import { useMemo, useState } from "react";
import { useLocation } from "react-router";
import styled from "styled-components";
import {
	ONLINE_LECTURE_PATH,
	LECTURE_ARCHIVE_PATH,
} from "constants/paths";
import {
	primaryWhite,
	primaryBlack,
	secondaryGrey,
} from "constants/color";

const STORAGE_KEY = "mobile_app_banner_dismissed";

const GOOGLE_PLAY_URL =
	"https://play.google.com/store/apps/details?id=ru.svetofor.app";
const APP_STORE_URL =
	"https://apps.apple.com/ru/app/%D0%BF%D0%B4%D0%B4-%D1%82%D0%B2/id1597304100";

const MOBILE_PAGES = [ONLINE_LECTURE_PATH, LECTURE_ARCHIVE_PATH];

const isMobileDevice = (): boolean =>
	/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
		navigator.userAgent
	);

const isIOS = (): boolean => /iPhone|iPad|iPod/i.test(navigator.userAgent);

const MobileAppBanner = () => {
	const location = useLocation();
	const isMobile = useMemo(isMobileDevice, []);
	const [dismissed, setDismissed] = useState(
		() => localStorage.getItem(STORAGE_KEY) === "1"
	);

	const isTargetPage = MOBILE_PAGES.some((path) =>
		location.pathname.startsWith(path)
	);

	if (!isMobile || dismissed || !isTargetPage) return null;

	const handleDismiss = () => {
		localStorage.setItem(STORAGE_KEY, "1");
		setDismissed(true);
	};

	return (
		<Overlay>
			<Banner>
				<CloseButton onClick={handleDismiss}>&times;</CloseButton>
				<Title>Рекомендуем скачать приложение</Title>
				<Description>
					Мобильный браузер не обеспечивает корректную работу
					видеотрансляций. <br />
					Для стабильного просмотра лекций
					используйте мобильное приложение ПДД ТВ.
				</Description>
				<StoreLinks>
					{isIOS() ? (
						<StoreLink href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
							<StoreBadge
								src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
								alt="App Store"
							/>
						</StoreLink>
					) : (
						<StoreLink href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
							<StoreBadge
								src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
								alt="Google Play"
							/>
						</StoreLink>
					)}
				</StoreLinks>
				<ContinueButton onClick={handleDismiss}>
					Продолжить в браузере
				</ContinueButton>
			</Banner>
		</Overlay>
	);
};

export default MobileAppBanner;

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 16px;
`;

const Banner = styled.div`
	position: relative;
	background: ${primaryWhite};
	border-radius: 16px;
	padding: 32px 24px 24px;
	max-width: 400px;
	width: 100%;
	text-align: center;
`;

const CloseButton = styled.button`
	position: absolute;
	top: 8px;
	right: 12px;
	background: none;
	border: none;
	font-size: 28px;
	color: ${primaryBlack};
	cursor: pointer;
	line-height: 1;
	padding: 4px;
`;

const Title = styled.h2`
	margin: 0 0 12px;
	font-size: 20px;
	font-weight: 600;
	color: ${primaryBlack};
`;

const Description = styled.p`
	margin: 0 0 20px;
	font-size: 14px;
	line-height: 1.5;
	color: ${primaryBlack};
	opacity: 0.75;
`;

const StoreLinks = styled.div`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-bottom: 16px;
`;

const StoreLink = styled.a`
	display: block;
`;

const StoreBadge = styled.img`
	height: 44px;
`;

const ContinueButton = styled.button`
	background: none;
	border: none;
	color: ${secondaryGrey};
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	padding: 8px 16px;
	text-decoration: underline;

	&:hover {
		opacity: 0.7;
	}
`;
